<template>
  <el-dialog
    :title="dialogTitle"
    :visible="visible"
    width="560px"
    :close-on-click-modal="false"
    class="page-dialog dialog-fixed-height"
    @close="handleClose"
  >
    <div class="dialog-content">
      <el-form
        ref="formRef"
        :model="localForm"
        :rules="formRules"
        label-position="right"
        label-width="120px"
        size="small"
        class="tier-form"
      >
        <el-form-item label="存款档位" prop="amountYuan">
          <el-input-number v-model="localForm.amountYuan" :min="0" class="input-number-sm" placeholder="例: 1000" />
          <span class="form-unit">{{ config.currency }}</span>
        </el-form-item>
        <el-form-item label="真金稽核倍数" prop="multiplier">
          <el-input-number v-model="localForm.multiplier" :min="0" class="input-number-sm" placeholder="0" />
          <span class="form-unit">倍</span>
        </el-form-item>
        <el-form-item label="赠送类型" prop="bonus_type">
          <el-radio-group v-model="localForm.bonus_type">
            <el-radio :label="1">比例赠送</el-radio>
            <el-radio :label="2">固定金额</el-radio>
            <el-radio :label="3">无赠送</el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="localForm.bonus_type === 1">
          <el-form-item label="赠送方式" prop="bonus_method">
            <el-radio-group v-model="localForm.bonus_method">
              <el-radio :label="1">固定</el-radio>
              <el-radio :label="2">范围内随机</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="赠送比例" prop="bonus_ratio_min">
            <span class="ratio-inputs">
              <el-input-number
                v-model="localForm.bonus_ratio_min"
                :min="0"
                :max="100"
                class="input-number-sm"
                placeholder="最小值"
              />
              <template v-if="localForm.bonus_method === 2">
                <span class="ratio-sep">-</span>
                <el-input-number
                  v-model="localForm.bonus_ratio_max"
                  :min="0"
                  :max="100"
                  class="input-number-sm"
                  placeholder="最大值"
                  @blur="() => formRef && formRef.validateField('bonus_ratio_min')"
                />
              </template>
              <span class="ratio-unit">%</span>
            </span>
          </el-form-item>
        </template>
        <el-form-item v-else-if="localForm.bonus_type === 2" label="赠送金额" prop="bonus_fixed_amount">
          <el-input-number
            v-model="localForm.bonus_fixed_amountYuan"
            :min="0"
            class="input-number-sm"
            placeholder="0"
          />
          <span class="form-unit">{{ config.currency }}</span>
        </el-form-item>
        <el-form-item label="赏金稽核倍数" prop="bonus_multiple">
          <el-input-number v-model="localForm.bonus_multiple" :min="0" class="input-number-sm" placeholder="0" />
          <span class="form-unit">倍</span>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="localForm.status">
            <el-radio :label="1">禁用</el-radio>
            <el-radio :label="2">启用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="适用对象" prop="vips" class="form-item-vips">
          <div class="vips-wrap">
            <el-checkbox :value="isAllVipChecked" @change="handleAllVipChange">全选</el-checkbox>
            <el-checkbox-group v-model="localForm.vips" class="vips-group">
              <el-checkbox v-for="item in vipOptions" :key="item.value" :label="item.value">
                {{ item.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, computed, getCurrentInstance } from 'vue'
import { getPayinBonus, createPayinBonus, updatePayinBonus } from '@/api/finance/payinBonus'
import { centsToYuanNumber, yuanToCents } from '@/utils/amount'
import { useVipOptions } from '@/composables/useVipOptions'
import config from '@/config'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create',
  },
  editId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['update:visible', 'success'])

const { proxy } = getCurrentInstance()
const formRef = ref(null)
const loading = ref(false)
const { vipOptions } = useVipOptions()

const dialogTitle = computed(() => (props.mode === 'edit' ? '档位设置' : '新增档位'))

const localForm = reactive({
  id: null,
  amountYuan: null,
  bonus_type: 1,
  bonus_method: 1,
  bonus_ratio_min: null,
  bonus_ratio_max: null,
  bonus_fixed_amountYuan: null,
  multiplier: 0,
  bonus_multiple: 0,
  status: 2,
  vips: [],
})

const isAllVipChecked = computed(() => {
  return localForm.vips.length === (vipOptions.value?.length || 0)
})
const formRules = {
  amountYuan: [
    { required: true, message: '请输入存款档位', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value != null && value !== '' && Number(value) <= 0) {
          callback(new Error('存款档位必须大于0'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  bonus_type: [{ required: true, message: '请选择赠送类型', trigger: 'change' }],
  bonus_ratio_min: [
    {
      validator: (rule, value, callback) => {
        if (localForm.bonus_type !== 1) return callback()
        if (localForm.bonus_method !== 2) return callback()
        const minVal =
          localForm.bonus_ratio_min != null && localForm.bonus_ratio_min !== ''
            ? Number(localForm.bonus_ratio_min)
            : null
        const maxVal =
          localForm.bonus_ratio_max != null && localForm.bonus_ratio_max !== ''
            ? Number(localForm.bonus_ratio_max)
            : null
        if (minVal == null || maxVal == null) return callback()
        if (minVal >= maxVal) {
          callback(new Error('赠送比例最小值必须小于最大值'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  multiplier: [{ required: true, message: '请输入真金稽核倍数', trigger: 'blur' }],
  bonus_multiple: [{ required: true, message: '请输入赏金稽核倍数', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }],
}

function parseVipsString(str) {
  if (str == null || str === '') return []
  return String(str)
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !Number.isNaN(n))
}

function buildPayload() {
  const payload = {
    amount: yuanToCents(localForm.amountYuan),
    bonus_type: localForm.bonus_type,
    bonus_method: localForm.bonus_type === 1 ? localForm.bonus_method : 1,
    bonus_ratio_min: localForm.bonus_type === 1 ? Number(localForm.bonus_ratio_min) : undefined,
    bonus_ratio_max:
      localForm.bonus_type === 1 && localForm.bonus_method === 2 ? Number(localForm.bonus_ratio_max) : undefined,
    bonus_fixed_amount: localForm.bonus_type === 2 ? yuanToCents(localForm.bonus_fixed_amountYuan) : undefined,
    multiplier: Number(localForm.multiplier) || 0,
    bonus_multiple: Number(localForm.bonus_multiple) || 0,
    status: localForm.status,
    vips: Array.isArray(localForm.vips) ? localForm.vips.sort((a, b) => a - b).join(',') : '',
  }
  if (props.mode === 'edit' && localForm.id != null) {
    payload.id = localForm.id
  }
  return payload
}

function resetForm() {
  Object.assign(localForm, {
    id: null,
    amountYuan: null,
    bonus_type: 1,
    bonus_method: 1,
    bonus_ratio_min: null,
    bonus_ratio_max: null,
    bonus_fixed_amountYuan: null,
    multiplier: 0,
    bonus_multiple: 0,
    status: 2,
    vips: [],
  })
}

watch(
  () => [props.visible, props.mode, props.editId],
  async () => {
    if (props.visible) {
      resetForm()
      if (props.mode === 'edit' && props.editId != null) {
        loading.value = true
        try {
          const res = await getPayinBonus({ id: props.editId })
          const raw = (res && res.data) || res
          const data = raw.data != null ? raw.data : raw
          if (data) {
            localForm.id = data.id
            localForm.amountYuan = centsToYuanNumber(data.amount)
            localForm.bonus_type = data.bonus_type ?? 1
            localForm.bonus_method = data.bonus_method ?? 1
            localForm.bonus_ratio_min = data.bonus_ratio_min != null ? Number(data.bonus_ratio_min) : null
            localForm.bonus_ratio_max = data.bonus_ratio_max != null ? Number(data.bonus_ratio_max) : null
            localForm.bonus_fixed_amountYuan =
              data.bonus_fixed_amount != null ? centsToYuanNumber(data.bonus_fixed_amount) : null
            // 兼容后端字段：multiplier/bonus_multiple（新） vs real_money_audit_multiplier/bonus_audit_multiplier（旧）
            localForm.multiplier =
              data.multiplier != null
                ? Number(data.multiplier)
                : data.real_money_audit_multiplier != null
                ? Number(data.real_money_audit_multiplier)
                : 0
            localForm.bonus_multiple =
              data.bonus_multiple != null
                ? Number(data.bonus_multiple)
                : data.bonus_audit_multiplier != null
                ? Number(data.bonus_audit_multiplier)
                : 0
            localForm.status = data.status ?? 2
            localForm.vips = parseVipsString(data.vips)
          }
        } catch (e) {
          console.error('获取档位详情失败', e)
          proxy.$message.error('获取档位详情失败')
        } finally {
          loading.value = false
        }
      }
    }
  },
  { immediate: true }
)

function handleAllVipChange(checked) {
  if (checked) {
    localForm.vips = (vipOptions.value || []).map(o => o.value)
  } else {
    localForm.vips = []
  }
}

function handleClose() {
  emit('update:visible', false)
}

function handleConfirm() {
  formRef.value.validate(valid => {
    if (!valid) return
    if (localForm.bonus_type === 1) {
      if (localForm.bonus_ratio_min == null && localForm.bonus_ratio_max == null) {
        proxy.$message.warning('请输入赠送比例')
        return
      }
    } else if (localForm.bonus_type === 2) {
      if (localForm.bonus_fixed_amountYuan == null) {
        proxy.$message.warning('请输入赠送金额')
        return
      }
    }
    loading.value = true
    const payload = buildPayload()
    const api = props.mode === 'edit' ? updatePayinBonus : createPayinBonus
    api(payload)
      .then(res => {
        const code = res && res.code
        if (code !== undefined && code !== 0) {
          throw new Error(res.msg || res.message || '保存失败')
        }
        proxy.$message.success('保存成功')
        emit('success')
        handleClose()
      })
      .catch(err => {
        console.error('保存档位失败', err)
        proxy.$message.error(err?.response?.data?.message || err.message || '保存失败')
      })
      .finally(() => {
        loading.value = false
      })
  })
}
</script>

<style lang="scss" scoped>
.dialog-fixed-height ::v-deep .el-dialog {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  margin: auto;
}
.dialog-fixed-height ::v-deep .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.tier-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}
.tier-form ::v-deep .el-form-item__label {
  padding-right: 12px;
}

.input-number-sm {
  width: 140px;
}
.tier-form ::v-deep .input-number-sm.el-input-number {
  width: 140px;
}

.ratio-inputs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.ratio-sep {
  color: #909399;
}
.ratio-unit {
  color: #606266;
  margin-left: 2px;
}
.form-unit {
  margin-left: 8px;
  color: #606266;
  font-size: 12px;
}

.form-item-vips ::v-deep .el-form-item__content {
  line-height: 1.5;
}
.vips-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vips-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}
</style>
